// oracle value-slot adapter (T11, Docs/AutocompleteValueSlot-Plan.md) — oracle
// clone of the T8 pgsql exemplar (pgsqlValueSlotAdapter.ts). Turns the
// expression arm's existing column discovery into normalized
// ValueSlotColumnDescriptors for the shared policy (D10). CTE/derived
// projection goes through the SAME machinery the arm uses
// (`resolveCteColumns` / `inferSubqueryColumns`) — not reimplemented.
//
// Relation lookup mirrors the resolver's private `resolveRelation`: explicit
// schema → exact (schema, name) match; otherwise any name match. No
// search-path list (unlike pgsql) — Oracle resolves unqualified names via the
// connected session schema, and the snapshot is already scoped to it.
//
// Sequences and packages surface as `extraScalarSources`, sourced straight
// from the same cached-schema arrays the resolver's `emitSequencesAsCandidates`
// / `emitPackagesAsCandidates` read (`args.sequences` / `args.packages`) and
// emitting the IDENTICAL candidate shape those two functions produce. They
// are duplicated here (not imported) rather than exported from
// `oracleResolver.ts`, because that module imports `buildOracleValueSlotAdapter`
// from this file — importing back would create a module cycle. The plan's own
// wording anticipates this ("IF existing emitters' discovery is reusable");
// both shapes are pinned identical by the contract test alongside this file's
// header note. In no-LHS (predicate-head) mode sequences are suppressed —
// ORA-02287 (see the P8c note at `extraScalarSources` below).
//
// Synonym NAMES and DUAL are EXCLUDED as candidates (T11 scope): no
// synonym-name or DUAL candidates are ever routed into descriptors or
// `extraScalarSources` — mirrors the S0 guard's suppression of
// `emitSynonymsAsRelations` at the comparison-value operand. P8c
// (Docs/AutocompletePredicateHead-Plan.md, C1 flip-gate "Oracle synonym
// resolution") CHANGED the binding side: a synonym-bound alias binding
// (`FROM EMPS E` where `EMPS` is a private/public synonym for
// `HR.EMPLOYEES`) now resolves to its target table's columns in
// `collectScopedColumns`, via the SAME chain `emitAliasColumns` uses
// (direct snapshot match → synonym chain, private over public, unqualified
// names only → virtual-column fallback). Pre-P8c the adapter never received
// `args.synonyms` at all, which merely degraded the LIVE value slot
// (columns of synonym-bound bindings were missing from an otherwise
// column-rich list) but would have left a predicate head near-EMPTY on a
// synonym-heavy schema — at a head the adapter IS the candidate source.
// Remaining gap (pre-existing, deliberately unchanged): LHS resolution
// (`resolveLhsInScope`) does not follow the synonym chain, so a
// comparison LHS owned by a synonym-bound binding still yields the neutral
// `'unknown'` scope and no `fkPartnerOfLhs` flags — the shared resolver is
// cross-engine and pinned byte-identical by the paired parity tests.
//
// P6c (Docs/AutocompletePredicateHead-Plan.md, PH-D3) — oracle clone of the
// P5 pgsql exemplar's no-LHS mode: `OracleAdapterSources` splits out
// everything LHS-independent, and `buildOracleValueSlotAdapter` gains a
// second typed overload (`OraclePredicateHeadContext`, `slot?: undefined`)
// so a bare predicate head can reuse this SAME adapter without an LHS
// operand. See `collectOracleScalarRoutineDescriptors` for why
// `returnsBooleanish` (PH-D4/F6) is left unset here.

import type { AliasBinding, CompletionCandidate, CteBinding, FkEdge } from '@/core/engines';
import type {
  OracleSchemaPackage,
  OracleSchemaSequence,
  OracleSchemaSynonym,
} from '@/features/connections/services/language-services/oracleSchemaTypes';
import type { ValueSlotInfo } from '@/features/sql-grammar/model';
import { applyPivotTransform, parsePivotClause } from '../pivot/parsePivotClause';
import { inferSubqueryColumns } from '../resolver/inferSubqueryColumns';
import { resolveCteColumns } from '../resolver/resolveCteColumns';
import { resolveLhsInScope, type ScopedOwner } from '../valueSlot/scopedResolve';
import {
  coarseTypeOfDataType,
  isBooleanishDataType,
  type ValueSlotColumnDescriptor,
  type ValueSlotEngineAdapter,
  type ValueSlotRoutineDescriptor,
  type ValueSlotScope,
  type ValueSlotVirtualColumn,
} from '../valueSlot/types';
import { PIVOT_SYNTHETIC_ALIAS } from '../resolver/aliasScope';
import { foldIdentifier, formatIdentifier } from './oracleIdentifiers';

type AdapterTable = {
  readonly name: string;
  readonly schema?: string;
  readonly columns: ReadonlyArray<{ readonly name: string; readonly dataType: string }>;
};
type AdapterRoutine = {
  readonly name: string;
  readonly schema?: string;
  readonly kind: string;
  readonly returns?: string;
};

/** Everything the adapter needs that is independent of the LHS operand. */
type OracleAdapterSources = {
  readonly text: string;
  readonly aliasBindings: readonly AliasBinding[];
  readonly cteBindings: readonly CteBinding[];
  readonly snapshotTables: ReadonlyArray<AdapterTable>;
  readonly snapshotRoutines: ReadonlyArray<AdapterRoutine>;
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly sequences: readonly OracleSchemaSequence[];
  readonly packages: readonly OracleSchemaPackage[];
  /** P8c (C1 flip-gate) — the cached synonym list the resolver already
   *  passes to `emitAliasColumns`/`emitSynonymsAsRelations`. Consumed ONLY
   *  to resolve synonym-bound alias BINDINGS to their target's columns;
   *  synonym names themselves never become candidates here. */
  readonly synonyms: readonly OracleSchemaSynonym[];
  /** C2 P1-1 — resolver-supplied fallback for bindings whose relation is a
   *  VIRTUAL data-dictionary view (USER_TABLES, ALL_OBJECTS, ...): a closure
   *  over the resolver's own static column map. Consulted only when the
   *  snapshot lookup yields nothing. */
  readonly virtualColumnsFor?: (binding: AliasBinding) => readonly ValueSlotVirtualColumn[] | undefined;
};

/** Scalar-value mode: a slot is REQUIRED. */
export type OracleValueSlotContext = OracleAdapterSources & {
  readonly slot: ValueSlotInfo;
};

/** Predicate-head mode (PH-D3, P1-sol F7): NO slot — a bare boolean-expression
 *  head has no LHS operand, so the scope resolves to
 *  `{ lhsType: 'unknown', lhsBooleanish: false }` and no column is ever
 *  flagged `fkPartnerOfLhs`. Declared as `slot?: undefined` rather than by
 *  omission so the overloads stay mutually exclusive: a caller passing a slot
 *  cannot land on this signature, and an existing scalar-value caller that
 *  DROPPED its slot fails to compile instead of silently degrading to
 *  no-LHS discovery. */
export type OraclePredicateHeadContext = OracleAdapterSources & {
  readonly slot?: undefined;
};

/** Implementation-side union — never the public parameter type. */
type OracleAdapterContext = OracleValueSlotContext | OraclePredicateHeadContext;

/** Same bareword-UPPER canonicalization as the resolver's private
 *  `canonicalizeInferredOracleName` (duplicated here for the same
 *  module-cycle reason as the sequence/package emitters — see the file
 *  header). Applied ONLY to the pivoted projection (P8c), so non-pivot
 *  subquery labels keep the adapter's pre-existing raw spelling. */
const PIVOTED_BAREWORD_RE = /^[A-Za-z][A-Za-z0-9_$#]*$/;
function canonicalizePivotedOracleName(name: string): string {
  if (name.length === 0 || !PIVOTED_BAREWORD_RE.test(name)) return name;
  return name.toUpperCase();
}

/** Relation lookup — parity with the resolver's private `resolveRelation`
 *  (explicit schema → exact; else any name match; no search-path list). */
function lookupRelation(
  tables: ReadonlyArray<AdapterTable>,
  binding: AliasBinding,
): AdapterTable | undefined {
  const nameFolded = foldIdentifier(binding.relation);
  if (binding.schema) {
    const schemaFolded = foldIdentifier(binding.schema);
    return tables.find(
      (t) =>
        foldIdentifier(t.name) === nameFolded && foldIdentifier(t.schema ?? '') === schemaFolded,
    );
  }
  return tables.find((t) => foldIdentifier(t.name) === nameFolded);
}

/** P8c (C1 flip-gate "Oracle synonym resolution") — resolve a table/view
 *  binding whose relation is a SYNONYM to its target snapshot table. Mirrors
 *  the resolver's `emitAliasColumns` fall-through exactly: only unqualified
 *  relations enter the chain (a schema-qualified `HR.EMPS` is never a synonym
 *  reference), a private synonym wins over a public one with the same name
 *  (`findSynonymByName`'s two-pass rule), and the target resolves by exact
 *  `(targetSchema, targetName)` snapshot match. */
function resolveSynonymTarget(
  tables: ReadonlyArray<AdapterTable>,
  synonyms: readonly OracleSchemaSynonym[],
  binding: AliasBinding,
): AdapterTable | undefined {
  if (binding.schema) return undefined;
  const nameFolded = foldIdentifier(binding.relation);
  const priv = synonyms.find((s) => !s.isPublic && s.name.toUpperCase() === nameFolded);
  const syn = priv ?? synonyms.find((s) => s.isPublic && s.name.toUpperCase() === nameFolded);
  if (!syn) return undefined;
  const targetName = syn.targetName.toUpperCase();
  const targetSchema = syn.targetSchema.toUpperCase();
  return tables.find(
    (t) =>
      foldIdentifier(t.name) === targetName && foldIdentifier(t.schema ?? '') === targetSchema,
  );
}

function fkPartner(
  fks: readonly FkEdge[],
  lhs: { table: AdapterTable; column: string },
  cand: { table: AdapterTable; column: string },
): boolean {
  const match = (
    e: FkEdge,
    a: { table: AdapterTable; column: string },
    b: { table: AdapterTable; column: string },
  ) =>
    e.fromColumns.length === 1 &&
    e.fromTable.toLowerCase() === a.table.name.toLowerCase() &&
    e.fromColumns[0]!.toLowerCase() === a.column.toLowerCase() &&
    e.toTable.toLowerCase() === b.table.name.toLowerCase() &&
    e.toColumns[0]!.toLowerCase() === b.column.toLowerCase();
  return fks.some((e) => match(e, lhs, cand) || match(e, cand, lhs));
}

/** Resolve the slot's LHS to (owners, coarse type, boolean-ish) — the D5
 *  all-agree rule collapses disagreeing owners to 'unknown'. */
function resolveLhs(args: OracleAdapterContext): {
  owners: readonly ScopedOwner[];
  scope: ValueSlotScope;
} {
  // No slot (predicate-head mode) → no LHS → the neutral scope, via the same
  // path a slot with an unresolvable LHS already took.
  const lhs = args.slot?.lhs;
  if (!lhs) return { owners: [], scope: { lhsType: 'unknown', lhsBooleanish: false } };
  const owners = resolveLhsInScope({
    lhs,
    aliasBindings: args.aliasBindings,
    cteBindings: args.cteBindings,
    snapshotTables: args.snapshotTables.map((t) => ({
      name: t.name,
      schema: t.schema,
      kind: 'table' as const,
      columns: t.columns.map((c) => ({
        name: c.name, dataType: c.dataType, nullable: true, isPrimaryKey: false, ordinal: 0,
      })),
    })),
    source: args.text,
    engineId: 'oracle',
    fold: foldIdentifier,
  });
  const types = owners.map((o) =>
    coarseTypeOfDataType('dataType' in o.column ? o.column.dataType : undefined),
  );
  const allAgree = types.length > 0 && types.every((t) => t === types[0]);
  const bools = owners.map((o) =>
    isBooleanishDataType('dataType' in o.column ? o.column.dataType : undefined, 'oracle'),
  );
  return {
    owners,
    scope: {
      lhsType: allAgree ? types[0]! : 'unknown',
      lhsBooleanish: bools.length > 0 && bools.every(Boolean),
    },
  };
}

/** Same candidate shape as the resolver's private `emitSequencesAsCandidates`
 *  — see file header for why this is duplicated rather than imported. */
function sequenceCandidates(sequences: readonly OracleSchemaSequence[]): CompletionCandidate[] {
  return sequences.map((s) => ({
    label: s.name,
    insertText: formatIdentifier(s.name),
    isSnippet: false,
    kind: 'system-object',
    tier: 3,
    detail: 'Sequence',
    filterText: s.name,
  }));
}

/** Same candidate shape as the resolver's private `emitPackagesAsCandidates`
 *  — see file header for why this is duplicated rather than imported. */
function packageCandidates(packages: readonly OracleSchemaPackage[]): CompletionCandidate[] {
  return packages.map((p) => ({
    label: p.name,
    insertText: formatIdentifier(p.name),
    isSnippet: false,
    kind: 'system-object',
    tier: 3,
    detail: `Package (${p.schema})`,
    filterText: p.name,
    ...(p.isSystemPackage ? { capExempt: true } : {}),
  }));
}

export function buildOracleValueSlotAdapter(args: OracleValueSlotContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildOracleValueSlotAdapter(args: OraclePredicateHeadContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildOracleValueSlotAdapter(args: OracleAdapterContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
} {
  const { owners, scope } = resolveLhs(args);
  const lhsOwner = owners.length === 1 ? owners[0] : undefined;
  const lhsTable =
    lhsOwner && (lhsOwner.binding.kind === 'table' || lhsOwner.binding.kind === 'view')
      ? lookupRelation(args.snapshotTables, lhsOwner.binding)
      : undefined;
  const lhsColumn = lhsOwner ? lhsOwner.column.name : undefined;

  const collectScopedColumns = (): readonly ValueSlotColumnDescriptor[] => {
    const out: ValueSlotColumnDescriptor[] = [];
    for (const b of args.aliasBindings) {
      const spelling = b.aliasSql ?? b.alias;
      const push = (name: string, dataType: string | undefined, table?: AdapterTable) => {
        out.push({
          label: name,
          insertBare: formatIdentifier(name),
          insertQualified: `${spelling}.${formatIdentifier(name)}`,
          owner: b,
          coarseType: coarseTypeOfDataType(dataType),
          booleanish: isBooleanishDataType(dataType, 'oracle'),
          fkPartnerOfLhs:
            table !== undefined &&
            lhsTable !== undefined &&
            lhsColumn !== undefined &&
            fkPartner(
              args.snapshotForeignKeys,
              { table: lhsTable, column: lhsColumn },
              { table, column: name },
            ),
        });
      };
      if (b.kind === 'subquery' || b.kind === 'derived') {
        const writerDeclared = b.syntheticColumns && b.syntheticColumns.length > 0;
        let names: readonly string[] = writerDeclared
          ? b.syntheticColumns!
          : b.bodyRange
            ? inferSubqueryColumns({
                source: args.text,
                bodyRange: b.bodyRange,
                engineId: 'oracle',
                snapshotTables: args.snapshotTables,
                cteBindings: args.cteBindings,
              })
            : [];
        // P8c — post-PIVOT shape parity with the resolver's `emitAliasColumns`
        // pivot arm (the C1 "PIVOT ranges" gap, closed for oracle here because
        // the head flip turned it from a value-slot degradation into a
        // regression of the pre-existing script.52 §3 pin): a
        // `( … ) PIVOT (…) p` binding's projection is the PIVOTED shape — the
        // FOR + aggregate-argument columns are consumed, the value aliases are
        // added — never the raw pre-pivot inference. Canonicalized with the
        // resolver's bareword-UPPER rule, applied to the pivoted list only so
        // non-pivot subquery labels keep the adapter's pre-existing spelling.
        // Mirrors the resolver exactly: writer-declared COLUMNS lists bypass
        // the transform there too (its syntheticColumns branch returns first).
        if (!writerDeclared && b.pivotRange) {
          const parsed = parsePivotClause(
            args.text.slice(b.pivotRange.start, b.pivotRange.end),
          );
          if (parsed) {
            names = applyPivotTransform(names, parsed).map(canonicalizePivotedOracleName);
          }
        }
        for (const n of names) push(n, undefined);
        if (names.length === 0) {
          for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        }
        continue;
      }
      if (b.kind === 'cte') {
        const cte = args.cteBindings.find(
          (c) => foldIdentifier(c.name) === foldIdentifier(b.relation),
        );
        if (!cte) continue;
        const names = resolveCteColumns({
          cte,
          source: args.text,
          engineId: 'oracle',
          snapshotTables: args.snapshotTables,
          cteBindings: args.cteBindings,
        });
        for (const n of names) push(n, undefined);
        continue;
      }
      const tbl = lookupRelation(args.snapshotTables, b);
      if (!tbl) {
        // P8c synonym chain (C1 flip-gate) — same fall-through order as the
        // resolver's `emitAliasColumns`: direct snapshot match above, synonym
        // chain here, virtual data-dictionary columns last. The TARGET table
        // is passed through so `fkPartnerOfLhs` matches against the real
        // table name FK edges reference.
        const synTarget = resolveSynonymTarget(args.snapshotTables, args.synonyms, b);
        if (synTarget) {
          for (const c of synTarget.columns) push(c.name, c.dataType, synTarget);
          continue;
        }
        for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        continue;
      }
      for (const c of tbl.columns) push(c.name, c.dataType, tbl);
    }
    return out;
  };

  const collectScalarRoutines = (): readonly ValueSlotRoutineDescriptor[] =>
    collectOracleScalarRoutineDescriptors(args.snapshotRoutines);

  // P8c (C1 flip-gate "Oracle sequences at heads") — sequences are SUPPRESSED
  // in no-LHS (predicate-head) mode: a sequence's only scalar continuations,
  // `seq.NEXTVAL`/`seq.CURRVAL`, raise ORA-02287 inside WHERE/HAVING/ON
  // predicates, so offering the bare sequence name at a head invites illegal
  // SQL. Scalar-value mode keeps them unchanged (SET/VALUES/RETURNING slots
  // are legal NEXTVAL positions — shipped behavior, pinned by
  // oracleResolver.valueSlot.contract.test.ts). Packages stay in both modes:
  // `pkg.fn(...)` calls are legal predicate operands.
  const atHead = args.slot === undefined;
  const extraScalarSources = (): readonly CompletionCandidate[] => [
    ...(atHead ? [] : sequenceCandidates(args.sequences)),
    ...packageCandidates(args.packages),
  ];

  // C2 head-parity — every user-typable binding, so the predicate-head policy
  // can emit a qualifier for a binding whose columns cannot be resolved
  // (relation outside snapshot + synonym chain). Mirrors the clause path's
  // `emitAliasNames`, which offered every alias at a bare `ON` regardless of
  // column resolution — the P8c flip must not lose those aliases. The
  // internal pivot alias is excluded exactly as `emitAliasNames` excludes it
  // (the user cannot type `__pivot`). Unread by the value-slot policy.
  const qualifierBindings = (): readonly AliasBinding[] =>
    args.aliasBindings.filter((b) => b.alias !== PIVOT_SYNTHETIC_ALIAS);

  return {
    adapter: {
      engine: 'oracle',
      collectScopedColumns,
      collectScalarRoutines,
      extraScalarSources,
      qualifierBindings,
    },
    scope,
  };
}

/** Scalar-routine descriptor mapping shared by this adapter's
 *  `collectScalarRoutines` closure and the resolver's T18b typed-VALUES-
 *  element call site (`emitTypedValuesElement` in `oracleResolver.ts`) — kept
 *  in one place so the two can't drift.
 *
 * P6c (PH-D4/P1-sol F6) — `returnsBooleanish` is deliberately LEFT UNSET
 * here, unlike the pgsql/mysql adapters. Oracle's schema-snapshot routine
 * metadata carries no return-type spelling at all in production: the
 * `ALL_ARGUMENTS` introspection query
 * (`oracleRoutineIntrospection.ts` → `fetchOracleRoutineParams`) filters
 * `POSITION > 0`, which excludes the RETURN pseudo-argument (`POSITION = 0`)
 * that would carry it — `RoutineMetadata.returns` (`src/types/electron.d.ts`)
 * is documented as populated for pg (`pg_get_function_result`) and mysql
 * (`DTD_IDENTIFIER`) only. Computing `isBooleanishDataType(r.returns,
 * 'oracle')` here would always evaluate against `undefined` and always
 * yield `false` — indistinguishable from "known not boolean" — so leaving
 * the field absent is the honest signal ("unknown/unset") rather than an
 * invented one, matching the plan's checkpoint-9 scoping (pg/mysql only).
 * `returnsCoarse` alone already recognizes a raw `'BOOLEAN'` spelling
 * (`coarseTypeOfDataType`'s `bool|boolean` branch), so a caller that DOES
 * hand this function a routine whose `returns` is literally `'BOOLEAN'`
 * (e.g. a test fixture) still gets boolean-return promotion at the
 * predicate-head policy — no widening is needed for oracle's native 23ai+
 * BOOLEAN return spelling. */
export function collectOracleScalarRoutineDescriptors(
  snapshotRoutines: ReadonlyArray<AdapterRoutine>,
): readonly ValueSlotRoutineDescriptor[] {
  return snapshotRoutines
    .filter((r) => r.kind !== 'procedure')
    .map((r) => ({
      label: r.name,
      insertText: `${r.name}($0)`,
      returnsCoarse: coarseTypeOfDataType(r.returns),
      returnsRaw: r.returns,
    }));
}
